import { useEffect, useMemo, useState } from "react";
import { ConfigProvider, Form, Input, Select, theme } from "antd";
import type { Rule } from "antd/es/form";
import i18n from "i18next";
import { translate } from "shared/languages";
import {
  DEFAULT_COUNTRY,
  countryDialOptions,
  formatAsYouType,
  isValidPhone,
  joinE164,
  nationalAfterEdit,
  splitE164,
} from "shared/utils/phone";
import type { CountryCode } from "libphonenumber-js/mobile";

/**
 * The one phone field in the admin platform.
 *
 * Replaces ten hand-rolled `<Input addonBefore="+62">` + `/^8\d{7,11}$/` pairs.
 * The Form value is a single E.164 string, exactly like the plain <Input> it
 * replaces, so every submit handler, DTO and API payload is untouched.
 */

/**
 * The countries Firebase will send an SMS OTP to: the SMS Region Policy
 * allowlist on project terasku-bd171 (Authentication → Settings → SMS Region
 * Policy). An admin-tier number receives an OTP on its first login, on a
 * password reset and after a change of number, so a number anywhere else
 * could be saved and then never verified. Keep this in step with the console.
 */
const SMS_OTP_COUNTRIES: readonly CountryCode[] = ["ID", "SG", "MY", "AU", "GB", "US"];

/**
 * Whether a complete number belongs to an SMS OTP country. Read from the
 * number itself rather than the picker, because a calling code is shared:
 * a Canadian number is "+1" like a US one, a Jersey number "+44" like a UK one.
 */
const isSmsOtpCountry = (value: string): boolean =>
  SMS_OTP_COUNTRIES.includes(splitE164(value).country);

interface PhoneInputProps {
  /** E.164, injected by Form.Item. */
  value?: string;
  /** E.164, injected by Form.Item. */
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * "addon"  — country Select as the Input's addonBefore. Visually a drop-in
   *            for the eight sites that used addonBefore="+62".
   * "inline" — borderless Select as an Input prefix behind a hairline, for the
   *            two auth pages (AuthUi's PhonePrefix treatment).
   */
  variant?: "addon" | "inline";
  /**
   * The number receives a Firebase SMS OTP (an admin-tier login number): the
   * picker offers only SMS_OTP_COUNTRIES. Pair with `phoneRules({ smsOtp })`.
   */
  smsOtp?: boolean;
}

const PhoneInput = ({
  value,
  onChange,
  disabled,
  placeholder,
  autoFocus,
  variant = "addon",
  smsOtp,
}: PhoneInputProps) => {
  const [country, setCountry] = useState<CountryCode>(DEFAULT_COUNTRY);
  const [national, setNational] = useState("");
  const { token } = theme.useToken();

  // Adopt an externally-set value (initialValues, setFieldsValue, a reset)
  // without clobbering what the user is part-way through typing: skip when the
  // incoming value is just the echo of our own last emit.
  useEffect(() => {
    const incoming = value ?? "";
    if (incoming === joinE164(country, national)) return;
    const parsed = splitE164(incoming);
    setCountry(parsed.country);
    setNational(parsed.nationalNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextCountry: CountryCode, nextNational: string) => {
    setCountry(nextCountry);
    setNational(nextNational);
    onChange?.(joinE164(nextCountry, nextNational));
  };

  const allOptions = useMemo(
    () =>
      countryDialOptions(i18n.language).map((o) => ({
        value: o.country,
        // Closed state shows just the dial code; the popup shows the country.
        label: `+${o.callingCode}`,
        title: `${o.flag} ${o.name} +${o.callingCode}`,
        search: `${o.name} ${o.country} +${o.callingCode} ${o.callingCode}`,
      })),
    [],
  );

  // A stored number from outside the list keeps its own country visible so
  // the field reads correctly; phoneRules({ smsOtp }) is what refuses it.
  const options = useMemo(
    () =>
      smsOtp
        ? allOptions.filter(
            (o) => SMS_OTP_COUNTRIES.includes(o.value) || o.value === country,
          )
        : allOptions,
    [allOptions, smsOtp, country],
  );

  // 88 fits a three-digit dial code ("+380") beside the arrow; the inline
  // variant used to be 74, which ellipsised those to "+3...".
  const selectWidth = 88;

  const countryControl = (
    <Select
      value={country}
      onChange={(next) => emit(next, national)}
      disabled={disabled}
      showSearch
      optionFilterProp="search"
      // 250 entries: the closed control stays narrow, the popup stays readable.
      popupMatchSelectWidth={320}
      style={{ width: selectWidth }}
      {...(variant === "inline" ? { variant: "borderless" as const } : {})}
      options={options}
      optionRender={(option) => <span>{option.data.title}</span>}
      aria-label={translate("label.country_code")}
    />
  );

  // As an Input prefix the Select sits INSIDE rc-input's affix wrapper, whose
  // onClick focuses the text input. Left to bubble, a click on the country
  // picker opens it and the wrapper's focus() closes it in the same tick, so
  // the dropdown never shows (measured: the picker was unopenable on the login
  // page). Stopping the click here keeps the wrapper's focus behaviour for
  // clicks on the rest of the control.
  //
  // The prefix also has to be no taller than one text line: the Select takes
  // the theme's controlHeight (48 on the auth pages), and rc-input ADDS the
  // wrapper's own padding around it, so the phone field came out ~26px taller
  // than the password field beside it. Sized to the input's text line here;
  // the popup keeps normal-height rows.
  const lineHeightPx = Math.round(token.fontSize * token.lineHeight);
  const inlineCountry = (
    <ConfigProvider
      theme={{
        components: {
          Select: {
            controlHeight: lineHeightPx,
            optionHeight: 32,
            optionPadding: "5px 12px",
            optionLineHeight: "22px",
          },
        },
      }}
    >
      <span onClick={(e) => e.stopPropagation()}>{countryControl}</span>
    </ConfigProvider>
  );

  const input = (
    <Input
      value={formatAsYouType(national, country)}
      onChange={(e) =>
        emit(
          country,
          nationalAfterEdit(formatAsYouType(national, country), e.target.value)
        )
      }
      disabled={disabled}
      autoFocus={autoFocus}
      placeholder={placeholder}
      inputMode="tel"
      {...(variant === "inline"
        ? { prefix: inlineCountry }
        : { addonBefore: countryControl })}
    />
  );

  // The Select inside an addonBefore needs its own borderless treatment so it
  // reads as part of the input rather than a second control.
  return variant === "addon" ? (
    <ConfigProvider
      theme={{ components: { Select: { colorBorder: "transparent" } } }}
    >
      {input}
    </ConfigProvider>
  ) : (
    input
  );
};

/**
 * The single validity rule, replacing ten copies of /^8\d{7,11}$/.
 *
 * Mobile-only for every country, which is what that regex guaranteed for
 * Indonesia: customers and instructors receive a login code on this number,
 * admins receive an SMS OTP, and a studio number becomes a wa.me link.
 */
export const phoneRules = (
  opts: { required?: boolean; smsOtp?: boolean } = {},
): Rule[] => {
  const rules: Rule[] = [];
  if (opts.required !== false) {
    rules.push({
      required: true,
      message: `${translate("form.error.input")} ${translate(
        "label.phone_number",
      )} ${translate("form.error.input_end")}`,
    });
  }
  rules.push({
    validator: (_, value) =>
      !value || isValidPhone(value)
        ? Promise.resolve()
        : Promise.reject(new Error(translate("form.error.phone_number"))),
  });
  if (opts.smsOtp) {
    // Silent on an invalid number, which the rule above already reports.
    rules.push({
      validator: (_, value) =>
        !value || !isValidPhone(value) || isSmsOtpCountry(value)
          ? Promise.resolve()
          : Promise.reject(new Error(translate("form.error.phone_number_sms_otp"))),
    });
  }
  return rules;
};

/** Convenience wrapper for the common labelled field. */
export const PhoneFormItem = ({
  name = "phone_number",
  required,
  ...props
}: PhoneInputProps & { name?: string; required?: boolean }) => (
  <Form.Item
    name={name}
    label={translate("label.phone_number")}
    rules={phoneRules({ required, smsOtp: props.smsOtp })}
  >
    <PhoneInput {...props} />
  </Form.Item>
);

export default PhoneInput;
